/* global LogicSetHelper */
import React from 'react';
import PropTypes from 'prop-types';

import MTSClient from '../../../mts-client';

// Components
import { StatusPill } from '../../../components/StatusPill';

import ResourceImportWorkflowStepOne from './StepOne';
import ResourceImportWorkflowStepTwo from './StepTwo';
import ResourceImportWorkflowStepThree from './StepThree';

// Helpers
import { getLogicSetStatus } from '../../../common/helpers/logicSetHelpers';

class ImportWorkflow extends React.Component {
    constructor(props) {
        super(props);

        this.stripSingleResourceForInsert = this.stripSingleResourceForInsert.bind(this);
        this.prepareImportedResourceForSave = this.prepareImportedResourceForSave.bind(this);
        this.submitRefSelections = this.submitRefSelections.bind(this);
        this.showSuccess = this.showSuccess.bind(this);
        this.showError = this.showError.bind(this);
        this.generateResourceOptions = this.generateResourceOptions.bind(this);
        this.generateReferenceOptions = this.generateReferenceOptions.bind(this);
        this.generateLogicSetOptions = this.generateLogicSetOptions.bind(this);
        this.setExistingResources = this.setExistingResources.bind(this);

        // TODO: Move these so as not to cause problems in updates
        this.resource = typeof this.props.resourceType === 'string' ? this.props.resourceType : 'externalReference';
        this.type = {
            externalReference: 'conditionreferences',
            internalReference: 'conditionreferences',
            logicSet: 'logicsets'
        }[this.resource];
        this.resourceName = {
            externalReference: 'External Reference',
            internalReference: 'Internal Reference',
            logicSet: 'Logic Set'
        }[this.resource];
        this.resourceNameShort = {
            externalReference: 'Reference',
            internalReference: 'Reference',
            logicSet: 'Logic Set'
        }[this.resource];
        this.uiConfig = null;
        this.userDTO = null;
        this.state = {
            activeStep: 1,
            existingResourceNames: [],
            // Specifications
            loadingSpecifications: false,
            selectedSpecification: null,
            specificationSelectionOptions: {},
            // External References
            loadingReferences: false,
            selectedResources: [],
            rawResources: null,
            referenceSelectionOptions: {},
            // Saving
            loadingSaveResults: false
        };
    }

    componentDidMount() {
        window.$(this.props.containerQuerySelector).on('hidden.bs.modal', (e) =>
            this.setState({
                activeStep: 1,
                selectedSpecification: null,
                selectedResources: []
            })
        );

        // Grab user and uiConfig from window
        const uiConfigs = JSON.parse(window.sessionStorage.getItem('uiConfigs'));
        if (typeof uiConfigs == 'object' && uiConfigs != null) {
            this.uiConfig = uiConfigs.rckms;
        }
        const user = JSON.parse(window.sessionStorage.getItem('rckmsUserDTO'));
        if (typeof user == 'object' && user != null) {
            this.userDTO = user;
        }
        this.mtsClient = new MTSClient(this.uiConfig, this.userDTO);
        this.setExistingResources(this.props.primaryKey);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.primaryKey !== this.props.primaryKey) {
            this.generateSpecificationOptions();
            this.setExistingResources(nextProps.primaryKey);
        }
    }

    showSuccess(value) {
        this.props.messenger.showMessage({ type: 'success', message: value, title: 'Import Success' });
    }

    showError(error) {
        this.props.messenger.showError({ type: 'error', message: error, title: 'Import Error' });
    }

    setRenderStep = (step) => this.state.activeStep !== step && this.setState({ activeStep: step });

    setLoadingState = (entity, loading) =>
        new Promise((resolve, reject) => this.setState({ [`loading${entity}`]: loading }, resolve()));

    setExistingResources(primaryKey = this.props.primaryKey) {
        if (this.type === 'conditionreferences') {
            this.mtsClient
                .fetchReferences(primaryKey, this.props.scope)
                .then((response) => {
                    const references = response.data;
                    let referenceNames = references.map((v) => v.name.toLowerCase());
                    this.setState({ existingResourceNames: referenceNames });
                })
                .catch((err) => {
                    console.log(err);
                    this.setState({ existingResourceNames: [] });
                });
        } else {
            this.mtsClient
                .fetchLogicSets(primaryKey)
                .then((response) => {
                    const references = response.data;
                    let referenceNames = references.map((v) => v.name.toLowerCase());
                    this.setState({ existingResourceNames: referenceNames });
                })
                .catch((err) => {
                    console.log(err);
                    this.setState({ existingResourceNames: [] });
                });
        }
    }

    // Return an object of reporting specification options for stage 2
    generateSpecificationOptions = () =>
        this.setLoadingState('Specifications', true).then(() =>
            this.mtsClient
                .fetchConditions(this.resource)
                .then((response) => {
                    let conditions = {};
                    const toCapitalizedStatus = (status) =>
                        status
                            .split('_')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ');
                    if (typeof response.data == 'object' && Array.isArray(response.data)) {
                        conditions = response.data.reduce((agg, v, i) => {
                            if (typeof agg[v.conditionId] == 'undefined' && v.conditionId != this.props.primaryKey) {
                                agg[v.conditionId] = {
                                    name: `${v.name}, ${v.version}, ${toCapitalizedStatus(v.status)}`,
                                    id: v.conditionId
                                };
                            }
                            return agg;
                        }, {});
                    }
                    this.setState({ specificationSelectionOptions: conditions }, () => {
                        this.setLoadingState('Specifications', false);
                    });
                })
                .catch((error) => {
                    console.log(error);
                    this.setState({ error: 'error' });
                })
        );

    // onChange handler for stage 2
    updateSpecificationSelection = (event) =>
        this.setState({
            selectedSpecification: event.target.value
        });

    // Return an object of external reference options for stage 3
    generateResourceOptions = () =>
        ({
            externalReference: this.generateReferenceOptions,
            internalReference: this.generateReferenceOptions,
            logicSet: this.generateLogicSetOptions
        }[this.props.resourceType]());

    generateLogicSetOptions = async () => {
        try {
            await this.setLoadingState('Resources', true);
            const logicSetsResponse = await this.mtsClient.fetchLogicSets(this.state.selectedSpecification);
            const criteriaResponse = await this.mtsClient.fetchCriteria(this.state.selectedSpecification);
            const conditionSpecsResponse = await this.mtsClient.fetchConditionSpecifications(
                this.state.selectedSpecification
            );

            const reportingSpecs = conditionSpecsResponse.data.reduce(
                (acc, spec) => {
                    const targetIndex = acc.findIndex(
                        ({ conditionCriteriaRel }) => conditionCriteriaRel.relId === spec.relId
                    );
                    if (targetIndex < 0) return acc;
                    acc[targetIndex].conditionSpecifications.push(spec);

                    return acc;
                },
                [
                    ...criteriaResponse.data.map((data) => ({
                        conditionCriteriaRel: data,
                        conditionSpecifications: []
                    }))
                ]
            );
            const logicSetMap = LogicSetHelper.createConditionSpecMapBySetId({ reportingSpecs });

            let logicSets = {};
            if (typeof logicSetsResponse.data == 'object' && Array.isArray(logicSetsResponse.data)) {
                const rawResources = logicSetsResponse.data.reduce((agg, v) => {
                    if (typeof agg[v.setId] == 'undefined') {
                        agg[v.setId] = v;
                    }
                    return agg;
                }, {});
                logicSets = logicSetsResponse.data.reduce(
                    (agg, v, i) => {
                        if (typeof agg[v.setId] == 'undefined') {
                            const status = getLogicSetStatus({
                                logicSetDto: v,
                                excludeInactive: true,
                                isOperable: LogicSetHelper.isOperable({
                                    conditionSpecs: logicSetMap?.[v.setId] ?? []
                                })
                            });

                            if (!this.state.existingResourceNames.includes(v.name.toLowerCase())) {
                                agg.available[v.setId] = {
                                    name: v.name,
                                    'Reporter Type': (
                                        <>
                                            {v.reporterTypeDTO.name}&nbsp;&nbsp;
                                            <StatusPill size={0.8} fontSize={0.8} status={status} />
                                        </>
                                    ),
                                    description: v.description
                                };
                            } else {
                                agg.imported[v.setId] = {
                                    name: v.name,
                                    'Reporter Type': (
                                        <>
                                            {v.reporterTypeDTO.name}&nbsp;&nbsp;
                                            <StatusPill size={0.8} fontSize={0.8} status={status} />
                                        </>
                                    ),
                                    description: v.description
                                };
                            }
                        }
                        return agg;
                    },
                    { available: {}, imported: {} }
                );

                this.setState({ referenceSelectionOptions: logicSets, rawResources }, () => {
                    this.setLoadingState('Resources', false);
                    return;
                });
            }
        } catch (err) {
            console.log(err);
        }
    };

    generateReferenceOptions = () =>
        new Promise((resolve, reject) =>
            this.setLoadingState('Resources', true).then(() =>
                this.mtsClient
                    .fetchReferences(this.state.selectedSpecification, this.props.scope)
                    .then((response) => {
                        let references = {};
                        if (typeof response.data == 'object' && Array.isArray(response.data)) {
                            const rawResources = response.data.reduce((agg, v) => {
                                if (typeof agg[v.referenceId] == 'undefined') {
                                    agg[v.referenceId] = v;
                                }
                                return agg;
                            }, {});
                            references = response.data.reduce(
                                (agg, v, i) => {
                                    if (typeof agg[v.referenceId] == 'undefined') {
                                        if (!this.state.existingResourceNames.includes(v.name.toLowerCase())) {
                                            agg.available[v.referenceId] = {
                                                name: v.name,
                                                url: v.url,
                                                priority: v.priority,
                                                category: v.category,
                                                scope: v.scope,
                                                description: v.description,
                                                excerpt: v.excerpt,
                                                fileCount: v.fileCount,
                                                referenceFiles: v.referenceFiles
                                            };
                                        } else {
                                            agg.imported[v.referenceId] = {
                                                name: v.name,
                                                url: v.url,
                                                priority: v.priority,
                                                category: v.category,
                                                scope: v.scope,
                                                description: v.description,
                                                excerpt: v.excerpt,
                                                fileCount: v.fileCount,
                                                referenceFiles: v.referenceFiles
                                            };
                                        }
                                    }
                                    return agg;
                                },
                                { available: {}, imported: {} }
                            );
                            this.setState({ referenceSelectionOptions: references, rawResources }, () => {
                                this.setLoadingState('Resources', false);
                                resolve();
                            });
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        this.setState({ error: 'error' });
                    })
            )
        );

    // onChange handler for stage 3
    updateResourceSelection = (id) =>
        this.setState({
            selectedResources: this.state.selectedResources.includes(id)
                ? this.state.selectedResources.filter((v) => v !== id)
                : [...this.state.selectedResources, id]
        });

    // "Continue" functionality, callback data to knockout?
    submitRefSelections() {
        this.setLoadingState('SaveResults', true);
        this.prepareImportedResourceForSave().then((preppedData) => {
            const importCount = preppedData.length;

            this.mtsClient
                .importMany(preppedData, this.type)
                .then((test) => {
                    this.showSuccess(
                        `${importCount} ${this.resourceName}${importCount > 1 ? 's' : ''} imported successfully.`
                    );
                    this.setState(
                        {
                            existingResourceNames: [
                                ...this.state.existingResourceNames,
                                ...preppedData.map((v) => v.name.toLowerCase())
                            ]
                        },
                        () => {
                            setTimeout(() => {
                                this.props.parentRefresh();
                                if (typeof this.props.onImport === 'function') {
                                    this.props.onImport();
                                }
                            }, 300);
                            this.setLoadingState('SaveResults', false);
                        }
                    );
                })
                .catch((err) => {
                    this.showError(`One or more ${this.resourceName}s failed to import.`);
                    console.log(err);
                });
        });
    }

    stripSingleResourceForInsert(...args) {
        return {
            externalReference: this.stripSingleReferenceForInsert(...args),
            internalReference: this.stripSingleReferenceForInsert(...args),
            logicSet: this.stripSingleLogicSetForInsert(...args)
        }[this.props.resourceType];
    }

    /**
     * Active property reset on import to match the reporterType implemented
     * flag. This is to avoid importing a logic set as inactive if it had been
     * set to inactive on the originating RS
     *
     * @param {object} logicSet
     * @returns object
     */
    stripSingleLogicSetForInsert(logicSet) {
        delete logicSet.createId;
        delete logicSet.lastModId;
        delete logicSet.lastModDatetime;
        delete logicSet.createDatetime;
        delete logicSet.setId;
        delete logicSet.timeframe;
        delete logicSet.timeframeUnit;
        delete logicSet.uuid;
        let additionalValues = {};

        if ((!logicSet.scope || !['INTERNAL', 'EXTERNAL'].includes(logicSet.scope)) && logicSet.reporterTypeDTO) {
            additionalValues = {
                active: logicSet.reporterTypeDTO.implemented
            };
        }

        return {
            ...logicSet,
            ...{
                dtoState: 'NEW',
                conditionId: this.props.primaryKey
            },
            ...additionalValues
        };
    }

    stripSingleReferenceForInsert(reference) {
        delete reference.createId;
        delete reference.lastModId;
        delete reference.lastModDatetime;
        delete reference.createDatetime;
        delete reference.referenceId;
        delete reference.uuid;

        if (reference.referenceFiles) {
            reference.referenceFiles = reference.referenceFiles.map((file) => {
                delete file.createId;
                delete file.lastModId;
                delete file.lastModDatetime;
                delete file.createDatetime;
                delete file.referenceId;
                delete file.uuid;
                // delete file.fileId;
                return {
                    ...file,
                    ...{
                        dtoState: 'NEW'
                    }
                };
            });
        }

        return {
            ...reference,
            ...{
                dtoState: 'NEW',
                conditionId: this.props.primaryKey
            }
        };
    }
    prepareImportedResourceForSave() {
        return Promise.all(
            [...this.state.selectedResources]
                .map((v) => this.state.rawResources[v])
                .filter((v) => typeof v == 'object')
                .map(this.stripSingleResourceForInsert)
                .map((v) => {
                    if (v.referenceFiles) {
                        return Promise.all(
                            v.referenceFiles.map((referenceFile) => {
                                if (referenceFile.fileId) {
                                    // fetch any existing reference file's data and copy it over into this new reference file
                                    return this.mtsClient
                                        .fetchReferenceFile(referenceFile.fileId, 'conditionreferencefiles')
                                        .then((data) => {
                                            // get rid of fileId, so we don't confuse the backend
                                            delete referenceFile.fileId;
                                            referenceFile.sourceFile = data;
                                            return referenceFile;
                                        })
                                        .catch((err) => {
                                            console.error(err);
                                            return referenceFile;
                                        });
                                } else {
                                    return Promise.resolve(v);
                                }
                            })
                        ).then(() => v);
                    } else {
                        return Promise.resolve(v);
                    }
                })
        );
    }

    render() {
        return (
            <div
                id="links-references-add-modal"
                className={`modal-dialog modal-lg`}
                aria-modal="true"
                aria-labelledby="links-references-add-page-title"
                style={{
                    maxWidth: this.state.activeStep === 1 ? '400px' : '760px'
                }}
            >
                {
                    {
                        1: (
                            <ResourceImportWorkflowStepOne
                                setRenderStep={this.setRenderStep}
                                generateSpecificationOptions={this.generateSpecificationOptions}
                                resourceName={this.resourceName}
                                resourceNameShort={this.resourceNameShort}
                                defaultAddNew={this.props.defaultAddNew}
                                loading={this.state.loadingSpecifications}
                            />
                        ),
                        2: (
                            <ResourceImportWorkflowStepTwo
                                generateResourceOptions={this.generateResourceOptions}
                                loading={this.state.loadingResources}
                                onChange={this.updateSpecificationSelection}
                                specifications={this.state.specificationSelectionOptions}
                                resourceName={this.resourceName}
                                resourceNameShort={this.resourceNameShort}
                                selectedSpecification={this.state.selectedSpecification}
                                setRenderStep={this.setRenderStep}
                            />
                        ),
                        3: (
                            <ResourceImportWorkflowStepThree
                                loading={this.state.loadingSaveResults}
                                onChange={this.updateResourceSelection}
                                resources={this.state.referenceSelectionOptions}
                                selections={this.state.selectedResources}
                                resourceName={this.resourceName}
                                resourceNameShort={this.resourceNameShort}
                                setRenderStep={this.setRenderStep}
                                submitExtRefSelections={this.submitRefSelections}
                            />
                        )
                    }[this.state.activeStep]
                }
            </div>
        );
    }
}

ImportWorkflow.propTypes = {
    addNew: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object
};

export default ImportWorkflow;
